import backendHttpClient from '@services/_httpClient/backend.http-client';

import type { CreateServicePayload } from './dtos/create.dto';
import type { GetAllServicesParams } from './dtos/getAll.dto';
import type { GetAllServicesToSelectParams } from './dtos/getAllToSelect.dto';
import type { UpdateServicePayload } from './dtos/update.dto';
import type { ServiceRepository } from './repository';
import type {
  CreateServiceOutput,
  CreateServiceResponse,
} from './types/create.type';
import type {
  GetAllServicesOutput,
  GetAllServicesResponse,
} from './types/getAll.type';
import type {
  GetAllServicesToSelectOutput,
  GetAllServicesToSelectResponse,
} from './types/getAllToSelect.type';
import type {
  GetServiceByIdOutput,
  GetServiceByIdResponse,
} from './types/getById.type';
import type {
  UpdateServiceOutput,
  UpdateServiceResponse,
} from './types/update.type';

class ServiceService implements ServiceRepository {
  public async create(
    payload: CreateServicePayload,
  ): Promise<CreateServiceOutput> {
    const { data } = await backendHttpClient.post<
      CreateServiceResponse,
      CreateServicePayload
    >({ body: payload, path: 'services' });

    return { service: data.service };
  }

  public async getAll(
    params: GetAllServicesParams,
  ): Promise<GetAllServicesOutput> {
    const { data } = await backendHttpClient.get<
      GetAllServicesResponse,
      GetAllServicesParams
    >({ params, path: 'services' });

    return { count: data.count, list: data.list, pagination: data.pagination };
  }

  public async getAllToSelect(
    params: GetAllServicesToSelectParams,
  ): Promise<GetAllServicesToSelectOutput> {
    const { data } =
      await backendHttpClient.get<GetAllServicesToSelectResponse>({
        path: 'services/select',
        params,
      });

    const output: GetAllServicesToSelectOutput = {
      list: data.list.map(service => ({
        label: service.translations.en,
        value: service.id,
        type: service.type,
        workflow: service.workflow,
      })),
    };

    return output;
  }

  public async getById(id: string): Promise<GetServiceByIdOutput> {
    const { data } = await backendHttpClient.get<GetServiceByIdResponse>({
      path: `services/${id}`,
    });

    return { service: data.service };
  }

  public async update({
    id,
    translations,
  }: UpdateServicePayload): Promise<UpdateServiceOutput> {
    const { data } = await backendHttpClient.patch<
      UpdateServiceResponse,
      Pick<UpdateServicePayload, 'translations'>
    >({ body: { translations }, path: `services/${id}` });

    return { service: data.service };
  }

  public async archive(id: string): Promise<void> {
    await backendHttpClient.delete<void>({ path: `services/${id}` });
  }

  public async unarchive(id: string): Promise<void> {
    await backendHttpClient.patch<void>({ path: `services/${id}/unarchive` });
  }
}

export const serviceService = new ServiceService();
